import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { handleApiError, logError } from '../utils/errorHandler';
import { getTokenForApiCall } from './authService';
import { isAuthEnabled } from '../auth/authConfig';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';

// Store MSAL instance and accounts for token retrieval
let msalInstance: PublicClientApplication | null = null;
let msalAccounts: AccountInfo[] | null = null;

/**
 * Initialize MSAL context for API calls
 * Call this from a component that has access to useMsal()
 */
export const initializeMsalContext = (instance: PublicClientApplication, accounts: AccountInfo[]): void => {
  msalInstance = instance;
  msalAccounts = accounts;
};

export const getMsalContextForApiCalls = (): {
  instance: PublicClientApplication | null;
  accounts: AccountInfo[] | null;
} => ({
  instance: msalInstance,
  accounts: msalAccounts,
});

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL + '/api/v1' || 'http://127.0.0.1:8080/api',
  timeout: 45000, // 45 seconds global timeout (can be overridden per request)
  headers: {
    'Content-Type': 'application/json',
  },
});



// Request interceptor for adding auth tokens with automatic refresh
apiClient.interceptors.request.use(
  async (config) => {
    // Add authentication token if auth is enabled
    if (isAuthEnabled && msalInstance && msalAccounts) {
      try {
        const token = await getTokenForApiCall(msalInstance, msalAccounts);
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to acquire token:', error);
        // Let the request proceed without token, API will handle auth error
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(handleApiError(error));
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const processedError = handleApiError(error);
    logError(processedError, { url: error.config?.url, method: error.config?.method });
    return Promise.reject(processedError);
  }
);

/**
 * Generic API methods
 */
export const apiService = {
  // GET request
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw error; // Error already handled by interceptor
    }
  },

  // POST request
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      // Return full response for blob/file downloads
      if (config.responseType === 'blob') {
        return response;
      }
      return response.data;
    } catch (error) {
      throw error; // Error already handled by interceptor
    }
  },

  // PUT request
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error; // Error already handled by interceptor
    }
  },

  // DELETE request
  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw error; // Error already handled by interceptor
    }
  },

  // PATCH request
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error; // Error already handled by interceptor
    }
  },
};

/**
 * Example API service methods
 * These are placeholder methods that can be customized based on your API endpoints
 */
export const userService = {
  // Get user profile
  getUserProfile: async (userId) => {
    return await apiService.get(`/users/${userId}`);
  },

  // Update user profile
  updateUserProfile: async (userId, userData) => {
    return await apiService.put(`/users/${userId}`, userData);
  },

  // Get users list
  getUsers: async (params = {}) => {
    return await apiService.get('/users', { params });
  },
};

export const dataService = {
  // Generic data fetching
  fetchData: async (endpoint, params = {}) => {
    return await apiService.get(endpoint, { params });
  },

  // Generic data posting
  postData: async (endpoint, data) => {
    return await apiService.post(endpoint, data);
  },

  // Generic data updating
  updateData: async (endpoint, data) => {
    return await apiService.put(endpoint, data);
  },

  // Generic data deletion
  deleteData: async (endpoint) => {
    return await apiService.delete(endpoint);
  },
};

/**
 * Dashboard Trends Service
 * Handles trend chart data retrieval
 */
export const dashboardTrendsService = {
  // Get trend data for specific KPI or all
  getTrendData: async (lineCode, fromDate = null, toDate = null, graphType = 'all') => {
    const params = { line_code: lineCode, graph_type: graphType };
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    return await apiService.get('/dashboard/trends', { params });
  },
};

export default apiService;